/**
 * Ni² Contextual Workspace — a wrapper around the existing
 * Ni2Workspace that adds Files, Case-linked Folder, and Imaging
 * Viewer states without ever unmounting the underlying conversation.
 *
 * Layering strategy:
 *   • Ni2Workspace is always mounted (bottom layer). Its scroll
 *     position, streaming state, draft text and animations survive
 *     every navigation to Files or Imaging.
 *   • A "book turn" transition tilts the conversation face on the
 *     Y-axis (right-edge anchor) and reveals the Files face beneath.
 *   • The Imaging viewer replaces the Files face in-place using a
 *     restrained slide/fade — no additional page-turn on that hop.
 *
 * The wrapper is opt-in: when `enabled` is false it returns the raw
 * Ni2Workspace with no changes so the flag can turn the whole feature
 * off without touching call sites.
 */
import { useCallback, useMemo, useState, useEffect } from "react";
import Ni2Workspace from "@/pages/Ni2Workspace";
import CaseFilesBrowser from "@/components/ni2/CaseFilesBrowser";
import ImagingViewer from "@/components/ni2/ImagingViewer";
import { NI2_CONTEXTUAL_FILES_ENABLED } from "@/constants/featureFlags";

/* Anchor case for the demo — matches Ni2Workspace's demo conversation
   (Dr. Rao's VS-RAO-001 vestibular schwannoma). Selecting a different
   Ni² conversation later would flow the new active case ID down. */
const DEFAULT_CASE_ID = "VS-RAO-001";

const WORKSPACE_HEIGHT = "calc(100vh - 168px)";

export default function Ni2ContextWorkspace({
  onBack,
  activeCaseId = DEFAULT_CASE_ID,
  enabled = NI2_CONTEXTUAL_FILES_ENABLED,
}) {
  // Flag off → behave exactly like the original Ni2Workspace
  if (!enabled) {
    return <Ni2Workspace onBack={onBack} />;
  }

  return <Ni2ContextWorkspaceInner onBack={onBack} activeCaseId={activeCaseId} />;
}

function Ni2ContextWorkspaceInner({ onBack, activeCaseId }) {
  // "conversation" | "files" | "viewer"
  const [mode, setMode] = useState("conversation");
  const [selectedFile, setSelectedFile] = useState(null); // full file record (Imaging or demo)
  // Retained after closing the viewer so the Ni² composer inherits
  // multimodal context (activeImagingId + filenamePlaceholder) for
  // the next user message. Cleared only when the user explicitly
  // opens a different case or opens a different file.
  const [lastViewedFile, setLastViewedFile] = useState(null);

  const openFiles = useCallback(() => {
    setSelectedFile(null);
    setMode("files");
  }, []);

  const closeFiles = useCallback(() => {
    setMode("conversation");
    setSelectedFile(null);
  }, []);

  const openFile = useCallback((file) => {
    setSelectedFile(file);
    setLastViewedFile(file);
    setMode("viewer");
  }, []);

  const backToFolder = useCallback(() => {
    setSelectedFile(null);
    setMode("files");
  }, []);

  // If the active case changes upstream, drop the retained imaging
  // context — it no longer refers to this case.
  useEffect(() => {
    setLastViewedFile(null);
  }, [activeCaseId]);

  // The composer inherits imaging context from either the currently
  // open viewer OR the last viewer the user closed, so a natural
  // "view scan, close viewer, ask Ni² about it" flow still passes
  // active_imaging_id + placeholder to the backend.
  const composerFile = selectedFile || lastViewedFile;
  const composerImagingId =
    composerFile?.imagingId ||
    (composerFile && composerFile.source !== "demo" ? composerFile.fileId : null) ||
    null;
  const composerImagingPlaceholder =
    composerFile?.fileName || composerFile?.filenamePlaceholder || null;

  const conversationState = mode === "conversation" ? "closed" : "open";
  const filesState = mode === "conversation" ? "closed" : "open";

  return (
    <div
      className="nn-bookstage relative"
      style={{ height: WORKSPACE_HEIGHT }}
      data-testid="ni2-context-workspace"
      data-mode={mode}
    >
      {/* Layer 1 — conversation (always mounted, tilts away on turn) */}
      <div
        className="nn-bookface absolute inset-0"
        data-face="conversation"
        data-state={conversationState}
        aria-hidden={mode !== "conversation"}
      >
        <span className="nn-bookedge" aria-hidden="true" />
        <Ni2Workspace
          onBack={onBack}
          onOpenFiles={openFiles}
          filesActive={mode !== "conversation"}
          activeImagingId={composerImagingId}
          activeImagingPlaceholder={composerImagingPlaceholder}
        />
      </div>

      {/* Layer 2 — files / viewer face */}
      <div
        className="nn-bookface"
        data-face="files"
        data-state={filesState}
        aria-hidden={mode === "conversation"}
      >
        <div
          className="nn-glass min-h-0 flex-1 overflow-hidden"
          style={{ height: WORKSPACE_HEIGHT, borderRadius: 22 }}
        >
          {mode === "viewer" && selectedFile ? (
            <ImagingViewer
              imagingId={selectedFile.imagingId || (selectedFile.source !== "demo" ? selectedFile.fileId : null)}
              demoFile={selectedFile.source === "demo" ? selectedFile : null}
              onBack={backToFolder}
              onCollapse={closeFiles}
            />
          ) : (
            <CaseFilesBrowser
              caseId={activeCaseId}
              onBack={closeFiles}
              onSelectFile={openFile}
            />
          )}
        </div>
      </div>
    </div>
  );
}
